import {
  DateField,
  ExportButton,
  List,
  NumberField,
  SaveButton,
  TextField,
  getDefaultSortOrder,
  useSelect,
  useTable,
} from "@refinedev/antd";

import {
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  FormProps,
  Grid,
  Row,
  Select,
  Space,
  Table,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";

import { CrudFilters, IResourceComponentsProps, getDefaultFilter, useExport } from "@refinedev/core";
import dayjs from "dayjs";
import { IOrderItem, IVesselSpendReport } from "interfaces";
import React from "react";

export const VesselSpendReportList: React.FC<IResourceComponentsProps> = () => {
  const { tableProps, sorters, tableQuery: { isLoading, data }, searchFormProps, filters } =
    useTable<IVesselSpendReport>({
      resource: "vessel-spend-report",

      // permanentFilter: [
      //   {
      //     field: "orderStatus",
      //     operator: "eq",
      //     value: "fulfilled",
      //   },
      // ],
      syncWithLocation: false,

      onSearch: (params: any) => {
        const { vesselName, orderDate } = params;
        return [
          {
            field: "vesselName",
            operator: "eq",
            value: vesselName,
          },
          {
            field: "orderDate",
            operator: "gte",
            value: orderDate
              ? orderDate[0].startOf("day").toISOString()
              : undefined,
          },
          {
            field: "orderDate",
            operator: "lte",
            value: orderDate
              ? orderDate[1].endOf("day").toISOString()
              : undefined,
          },
        ];
      },

      pagination: {
        mode: "off"
      },

      sorters: {
        initial: [
          {
            field: "id",
            order: "desc",
          },
        ]
      }
    });

  const orders = data?.data ?? [];

  const calculateTotalSpend = (orders: Array<IVesselSpendReport>) => {
    const totals: Record<string, number> = {};

    for (const order of orders) {
      //if vessel does not exist in 'totals' - add it with a total of zero
      const vesselName = order?.vesselName ?? "undefined_vessel";
      const totalOrderValue = order?.totalOrderValue ?? 0;
      if (!totals[vesselName]) {
        totals[vesselName] = 0;
      }
      totals[vesselName] += parseFloat(totalOrderValue as any);
    }
    return totals;
  };

  const totals = calculateTotalSpend(orders);

  const { triggerExport, isLoading: isLoadingExport } =
    useExport<IVesselSpendReport>({
      mapData: (item) => {
        return {
          "UKHO Licence ID": item?.ukhoLicenceId || "",
          "Shipco Name": item?.shipcoName || "",
          "Vessel Name": item?.vesselName || "",
          "IMO Number": item?.imoNumber || "",
          "Order Date": item?.orderDate || "",
          "Currency": item?.orderCurrency || "",
          "Total Price": item?.totalOrderValue || "",
        };
      },
      unparseConfig: {
        header: true,
        columns: [
          "UKHO Licence ID",
          "Shipco Name",
          "Vessel Name",
          "IMO Number",
          "Order Date",
          "Currency",
          "Total Price",
        ],
      },
    });

  const breakpoint = Grid.useBreakpoint();

  const columns: ColumnsType<IVesselSpendReport> = [
    {
      title: "Vessel Name",
      dataIndex: "vesselName",
      key: "vesselName",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("vesselName", sorters),
      sorter: true,
    },
    {
      title: "IMO Number",
      dataIndex: "imoNumber",
      key: "imoNumber",
      width: 130,
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("imoNumber", sorters),
      sorter: true,
    },
    {
      title: "Order ID",
      dataIndex: "ukhoOrderId",
      key: "ukhoOrderId",
      width: 130,
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("ukhoOrderId", sorters),
      sorter: true,
    },
    {
      title: "Order Date",
      dataIndex: "orderDate",
      key: "orderDate",
      width: 140,
      render: (value) => <DateField format="DD-MMM-YYYY" value={value} />,
      defaultSortOrder: getDefaultSortOrder("orderDate", sorters),
      sorter: true,
    },
    {
      title: "Order Value",
      dataIndex: "totalOrderValue",
      key: "totalOrderValue",
      width: 140,
      render: (value) => (
        <NumberField
          value={value}
          options={{
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 2,
          }}
        />
      ),
      defaultSortOrder: getDefaultSortOrder("totalOrderValue", sorters),
      sorter: true,
    },
    {
      title: "Total Vessel Spend",
      dataIndex: "vesselName",
      key: "totalVesselSpend",
      width: 160,
      render: (value) => (
        <NumberField
          value={totals[value]}
          options={{
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 2,
          }}
        />
      ),
    },
  ];

  // Nested table columns (column conversion only per user instructions)
  const orderItemColumns: ColumnsType<IOrderItem> = [
    {
      title: "Product ID",
      dataIndex: "productId",
      key: "productId",
      render: (value) => <TextField value={value} />,
    },
    {
      title: "Product Name",
      dataIndex: "productName",
      key: "productName",
      render: (value) => <TextField value={value} />,
    },
  ];

  return (
    <List
      breadcrumb={false}
      headerProps={{
        title: "Report",
        subTitle: "vessel spend analysis",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <React.Fragment>
            <Space>
              <ExportButton onClick={triggerExport} loading={isLoadingExport} />
            </Space>
          </React.Fragment>
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Row gutter={[16, 16]}>
        <Col xl={6} lg={24} xs={24}>
          <Card type="inner" title={"Filter"}>
            <Filter formProps={searchFormProps} filters={filters || []} />
          </Card>
        </Col>
        <Col xl={18} xs={24}>
          <Table
            {...tableProps}
            columns={columns}
            rowKey="id"
            size="middle"
            scroll={{ x: 1200 }}
            sticky
            expandable={{
              expandedRowRender: !breakpoint.xs
                ? (record) => <OrderItems record={record} columns={orderItemColumns} />
                : undefined,
            }}
          />
        </Col>
      </Row>
    </List>
  );
};

const Filter: React.FC<{ formProps: FormProps; filters: CrudFilters }> = (
  props
) => {
  const { formProps, filters } = props;

  const { selectProps: vesselNameSelectProps } = useSelect<IVesselSpendReport>({
    resource: "vessel-spend-report",
    optionLabel: "vesselName",
    optionValue: "vesselName",

    pagination: {
      mode: "server",
      pageSize: 30
    }
  });

  const { RangePicker } = DatePicker;

  const orderDate = React.useMemo(() => {
    const start = getDefaultFilter("orderDate", filters, "gte");
    const end = getDefaultFilter("orderDate", filters, "lte");

    const startFrom = dayjs(start);
    const endAt = dayjs(end);

    if (start && end) {
      return [startFrom, endAt];
    }
    return undefined;
  }, [filters]);

  return (
    <Form
      layout="vertical"
      {...formProps}
      initialValues={{
        vesselName: getDefaultFilter("vesselName", filters),
        orderDate,
      }}
    >
      <Row gutter={[10, 0]} align="bottom">
        <Col xl={24} md={8} sm={12} xs={24}>
          <Form.Item label={"Vessel Name"} name="vesselName">
            <Select
              {...vesselNameSelectProps}
              allowClear
              // mode="multiple"
              placeholder="filter by vessel"
            />
          </Form.Item>
        </Col>
        <Col xl={24} md={8} sm={12} xs={24}>
          <Form.Item label={"Order Date"} name="orderDate">
            <RangePicker style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col xl={24} md={8} sm={12} xs={24}>
          <Form.Item>
            <SaveButton onClick={formProps.form?.submit} size="large" block>
              Filter
            </SaveButton>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

const OrderItems: React.FC<{ record: IVesselSpendReport; columns: ColumnsType<IOrderItem> }> = ({ record, columns }) => {
  const { tableProps } = useTable<IOrderItem>({
    resource: "order-item",
    syncWithLocation: false,

    filters: {
      permanent: [
        {
          field: "order.id",
          operator: "eq",
          value: record.id,
        },
      ]
    }
  });
  return (
    <Table
      {...tableProps}
      columns={columns}
      rowKey="id"
      pagination={false}
      footer={() => <Space direction="horizontal"></Space>}
    />
  );
};
